<template>
  <div class="trip-cost-calculator">
    <div class="calculator-content">
      <div class="calculator-header">
        <span class="calculator-title">Trip Cost Calculator</span>
        <button @click="closeCalculator" class="close-button">
          <inline-svg name="close" classes="w-3 h-3 fill-current"></inline-svg>
        </button>
      </div>

      <div v-if="routeDetails" class="route-details">
        <div class="flex flex-col items-center">
          <div class="detail-row">
              <span class="detail-label">Distance:</span>
              <span class="detail-value">{{ formatDistance(routeDetails.distance_miles) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Duration:</span>
              <span class="detail-value">{{ routeDetails.duration }}</span>
            </div>
        </div>
      </div>

      <div v-if="!canCalculateCosts" class="text-center">
        <p class="empty-message">
          Complete a valid route to calculate trip costs
        </p>
      </div>

      <div v-if="canCalculateCosts" class="calculator-form">
        <!-- Vehicle Type Selection -->
        <div class="vehicle-select-wrapper">
          <select v-model="selectedVehicleType" @change="loadVehicleCosts" class="vehicle-select">
            <option value="">Select Vehicle Type</option>
            <option v-for="vehicle in vehicleTypes" :key="vehicle.id" :value="vehicle.id">
              {{ vehicle.name }}
            </option>
          </select>
        </div>
        <!-- Cost Categories Form -->
        <div v-if="selectedVehicleType && costCategories.length > 0" class="cost-categories">
          <div class="categories-header">
            <label class="categories-label">Cost Categories (per mile)</label>
            <a
              v-if="showResetAll"
              href="#"
              class="reset-link"
              @click.prevent="resetAllCostInputs"
            >Reset All</a>
          </div>
          <div class="port-stats-table">
            <table>
              <tbody>
                <tr v-for="category in costCategories" :key="category.id">
                  <th>{{ category.name }}</th>
                  <td>
                    <div class="flex items-center space-x-1">
                      <span class="currency-symbol">$</span>
                      <input
                        v-model="costInputs[category.id]"
                        type="number"
                        step="0.0001"
                        min="0"
                        class="cost-input"
                        @input="onCostInput(category.id, $event)"
                        @blur="onCostBlur(category.id)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Calculate Button -->
          <button
            v-if="canCalculateTripCost && !tripCostResults"
            @click="calculateTripCost"
            class="calculate-button"
            :disabled="loading"
          >
            {{ loading ? 'Calculating...' : 'Calculate Trip Cost' }}
          </button>
        </div>

        <!-- Results -->
        <div v-if="tripCostResults" class="results-section">
          <h4 class="results-title">Trip Cost Breakdown</h4>
          <div class="port-stats-table">
            <table>
              <tbody>
                <tr v-for="breakdown in sortedCostBreakdown" :key="breakdown.category_id">
                  <th>{{ getCategoryName(breakdown.category_id) }}</th>
                  <td>${{ formatCurrency(breakdown.total_cost) }}</td>
                </tr>
                <tr class="total-row">
                  <th>Total Trip Cost</th>
                  <td class="total-cost">${{ formatCurrency(tripCostResults.total_cost, true) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import InlineSvg from '../InlineSvg';
import { getRouteDetails } from '../../stores/reatlas';

export default {
  name: 'TripCostCalculator',
  components: {
    InlineSvg
  },
  data() {
    return {
      vehicleTypes: [],
      costCategories: [],
      selectedVehicleType: 1,
      costInputs: {},
      defaultCostInputs: {},
      tripCostResults: null,
      errorMessage: '',
      loading: false,
      currentDirections: null
    };
  },
  computed: {
    ...mapState(['reatlas']),

    canCalculateCosts() {
      return this.currentDirections?.routes?.length > 0 && this.currentDirections?.status === 'OK';
    },

    routeDetails() {
      if (!this.canCalculateCosts) return null;
      return getRouteDetails(this.currentDirections);
    },

    canCalculateTripCost() {
      return this.selectedVehicleType &&
             this.costCategories.length > 0 &&
             Object.keys(this.costInputs).length > 0 &&
             Number(this.routeDetails?.distance_miles) > 0;
    },

    sortedCostBreakdown() {
      if (!this.tripCostResults || !this.tripCostResults.cost_breakdown) return [];
      const orderMap = {};
      this.costCategories.forEach((cat, idx) => {
        orderMap[cat.id] = idx;
      });
      return [...this.tripCostResults.cost_breakdown].sort(
        (a, b) => (orderMap[a.category_id] ?? 9999) - (orderMap[b.category_id] ?? 9999)
      );
    },

    showResetAll() {
      return Object.keys(this.costInputs).some(
        id => Number(this.costInputs[id]) !== Number(this.defaultCostInputs[id])
      );
    }
  },

  watch: {
    'reatlas.directions.directionsRenderer': {
      handler(dr) {
        if (dr && dr.getDirections) {
          const updateRoute = () => {
            const result = dr.getDirections();
            if (result && result.routes && result.routes[0] && result.routes[0].legs) {
              this.currentDirections = result;
              // Clear results when route changes
              this.tripCostResults = null;
            } else {
              this.currentDirections = null;
              this.tripCostResults = null;
            }
          };
          google.maps.event.addListener(dr, 'directions_changed', updateRoute);
          updateRoute();
        }
      },
      immediate: true
    },
    
    // Watch for route details changes to clear results
    'routeDetails.distance_miles'(newDistance, oldDistance) {
      if (newDistance !== oldDistance && this.tripCostResults) {
        // Route changed, clear old results
        this.tripCostResults = null;
      }
    }
  },

  mounted() {
    this.loadVehicleTransportCosts();
  },

  methods: {
    closeCalculator() {
      this.$emit('close-calculator');
    },

    async loadVehicleTransportCosts() {
      try {
        this.loading = true;
        const response = await axios.get('/api/vehicle-transport-costs');
        this.vehicleTypes = response.data.vehicle_types;
        this.costCategories = response.data.cost_categories;
        // If a vehicle type is already selected, load its costs
        if (this.selectedVehicleType) {
          this.loadVehicleCosts();
        }
      } catch (error) {
        console.error('Error loading vehicle transport costs:', error);
        this.errorMessage = 'Failed to load vehicle transport costs.';
      } finally {
        this.loading = false;
      }
    },

    loadVehicleCosts() {
      if (!this.selectedVehicleType) {
        this.costInputs = {};
        this.defaultCostInputs = {};
        return;
      }

      const selectedVehicle = this.vehicleTypes.find(v => v.id == this.selectedVehicleType);
      if (!selectedVehicle) return;

      this.costInputs = {};
      this.defaultCostInputs = {};
      selectedVehicle.cost_categories.forEach(category => {
        this.costInputs[category.id] = parseFloat(category.cost_per_mile).toFixed(3);
        this.defaultCostInputs[category.id] = parseFloat(category.cost_per_mile).toFixed(3);
      });
      // Force Vue reactivity
      this.costInputs = { ...this.costInputs };
      this.defaultCostInputs = { ...this.defaultCostInputs };
    },

    async calculateTripCost() {
      if (!this.canCalculateTripCost) return;

      // Proceed with calculation (email already checked in parent component)
      this.performCalculation();
    },

    async performCalculation() {
      if (!this.canCalculateTripCost) return;
      
      try {
        this.loading = true;
        this.errorMessage = '';

        const response = await axios.post('/api/vehicle-transport-costs/calculate', {
          vehicle_type_id: this.selectedVehicleType,
          distance_miles: this.routeDetails.distance_miles,
          costs: this.costInputs
        });

        this.tripCostResults = response.data;
      } catch (error) {
        console.error('Error calculating trip cost:', error);
        this.errorMessage = 'Failed to calculate trip cost. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    formatDistance(miles) {
      const num = Number(miles);
      if (Number.isNaN(num)) {
        return miles ?? '';
      }
      const rounded = Math.round(num);
      return rounded.toLocaleString() + ' Miles';
    },

    getCategoryName(categoryId) {
      const category = this.costCategories.find(c => c.id == categoryId);
      return category ? category.name : 'Unknown Category';
    },

    formatCurrency(amount, useGrouping = false) {
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return "0.00";
      }
      if (useGrouping) {
        return number.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
      return number.toFixed(2);
    },

    resetAllCostInputs() {
      this.costInputs = { ...this.defaultCostInputs };
      this.tripCostResults = null;
    },

    onCostInput(categoryId, event) {
      let value = event.target.value.replace(',', '.');
      // Only allow numbers and one dot
      value = value.replace(/[^0-9.]/g, '');
      // Prevent multiple dots
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      this.costInputs[categoryId] = value;
    },

    onCostBlur(categoryId) {
      let value = this.costInputs[categoryId];
      if (value !== undefined && value !== null && value !== '') {
        value = parseFloat(value);
        if (!isNaN(value)) {
          this.costInputs[categoryId] = value.toFixed(3);
        }
      }
    }
  }
};
</script>

<style scoped>
.trip-cost-calculator {
  width: 100%;
}

.calculator-content {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: var(--card);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.calculator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.calculator-title {
  font-weight: 600;
  color: var(--foreground);
  font-size: 1rem;
}

.close-button {
  cursor: pointer;
  color: var(--muted-foreground);
  transition: color 0.2s ease;
}

.close-button:hover {
  color: var(--foreground);
}

.route-details {
  padding: 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border);
  background-color: var(--muted);
  margin-bottom: 0.75rem;
}

.detail-row {
  font-size: 0.875rem;
  width: 100%;
  color: var(--foreground);
}

.detail-label {
  font-weight: 600;
  color: var(--foreground);
}

.detail-value {
  margin-left: 0.25rem;
  color: var(--muted-foreground);
}

.empty-message {
  font-size: 0.875rem;
  color: var(--muted-foreground);
}

.calculator-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.vehicle-select-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.vehicle-select {
  width: 100%;
  background-color: var(--input);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  color: var(--foreground);
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.vehicle-select:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
}

.cost-categories {
  display: flex;
  flex-direction: column;
}

.categories-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.categories-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--foreground);
  display: block;
}

.reset-link {
  font-size: 0.75rem;
  color: var(--primary);
  text-decoration: underline;
  cursor: pointer;
}

.reset-link:hover {
  opacity: 0.8;
}

.calculate-button {
  width: 100%;
  padding: 0.75rem 1.5rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--primary-foreground);
  background-color: var(--primary);
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 200ms ease;
}

.calculate-button:hover:not(:disabled) {
  opacity: 0.9;
}

.calculate-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.currency-symbol {
  font-size: 0.75rem;
  color: var(--muted-foreground);
}

.cost-input {
  height: 1.75rem;
  width: 6rem;
  text-align: left;
  font-size: 0.8125rem;
  background-color: var(--input);
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  color: var(--foreground);
  padding: 0.25rem 0.5rem;
  transition: all 0.2s ease;
  font-weight: 500;
}

.cost-input:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
}

.results-section {
  margin-top: 0.5rem;
}

.results-title {
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  color: var(--foreground);
}

.total-row th {
  font-weight: 700 !important;
}

.total-cost {
  font-weight: 700 !important;
  color: var(--primary) !important;
}

.error-message {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: var(--destructive);
  border: 1px solid var(--destructive);
  font-size: 0.75rem;
  color: var(--destructive-foreground);
  border-radius: 0.5rem;
}

/* Remove arrows from number inputs (all browsers) */
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}
input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
